// The guided tour's player (the tours are features/tours.ts): a strip under the bar with the step's card title,
// the tour's line for it and the scene it's in, then Back, Pause, Read more (the card in the Gallery) and Next.
// A step opens its card's state in place, as a Gallery card does (openLink), and holds once the scene has what
// it draws (url.ts's sceneSettled), then the tour moves on; a sheet over it (Gallery, ⌘K, help) holds it too.
// Where the tour is lives in the store (`tour`) and in the bar (`tour=<id>.<step>`), so a link opens it there.
// A scene's own tour (tours.ts sceneTour) plays the same way; its id is the scene's, and it exists once the gallery
// has loaded, which tourTo waits for.

import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Route, X } from 'lucide-react';
import { useAtlas } from '../app/store.ts';
import { sceneSettled } from '../app/url.ts';
import { journeyOf } from '../app/scene.ts';
import { useJourney } from './journey.ts';
import { SCENARIOS } from '../scenarios/index.ts';
import { Button, IconButton, Tip } from '../ui/primitives.tsx';
import { galleryIfLoaded, loadGallery, STATE_LABEL, type Gallery as GalleryData } from '../data/gallery.ts';
import { openLink } from './Gallery.tsx';
import { HOLD_S, TOURS, holdOf, isSceneTourId, linkOf, nextSceneTour, resolveTour, sceneTour } from './tours.ts';
import './web.css';

// The checks read the written tours and resolve their cards against the loaded gallery here (every step must name a card the
// gallery has, with the link it opens: gallery v2.54 renamed the Powers cards, and a manifest that moves on without tours.ts
// leaves step 2 with no card and no dive).
if (typeof window !== 'undefined') (window as unknown as { __tours?: { TOURS: typeof TOURS; linkOf: typeof linkOf; gallery: typeof galleryIfLoaded } }).__tours = { TOURS, linkOf, gallery: galleryIfLoaded };

/** The tail a journey step holds past the journey's end, s. */
const JOURNEY_TAIL_S = 3;

/** Open a tour at a step: the step's card state in the page, and the player on it. */
export async function tourTo(id: string, step: number, paused = false): Promise<void> {
  const g = await loadGallery();
  const tour = resolveTour(id, g);
  if (!tour) return;
  const i = Math.max(0, Math.min(tour.steps.length - 1, step));
  const st = tour.steps[i];
  if (!st) return;
  const item = g.items.find((it) => it.id === st.card);
  const link = item ? linkOf(item, st) : null;
  useAtlas.getState().setTour({ id, step: i, paused });
  if (link) openLink(link.q);
  else console.warn(`[tour] ${id}, step ${i + 1}: the gallery has no ${st.card}${st.link ? ` with a link “${st.link}”` : ''}`);
}

/** Start a tour from its first step: the written one by default, or a scene's own by the scene's id. */
export function startTour(id = 'start') {
  void tourTo(id, 0);
}

/** One step on (+1) or back (−1); on from the last step, the tour ends. */
export function tourGo(delta: number) {
  const s = useAtlas.getState();
  const pos = s.tour;
  if (!pos) return;
  const tour = resolveTour(pos.id, galleryIfLoaded());
  const next = pos.step + delta;
  if (!tour || next >= tour.steps.length) {
    s.setTour(null);
    return;
  }
  if (next < 0) return;
  void tourTo(pos.id, next, pos.paused);
}

export function Tour() {
  const pos = useAtlas((s) => s.tour);
  const settled = useAtlas(sceneSettled);
  const covered = useAtlas((s) => s.galleryOpen || s.paletteOpen || s.helpOpen);
  const setTour = useAtlas((s) => s.setTour);
  const [data, setData] = useState<GalleryData | null>(null);
  useEffect(() => {
    if (pos && !data) void loadGallery().then(setData);
  }, [pos, data]);
  const tour = resolveTour(pos?.id, data);
  // A link can ask a scene's tour for a step it hasn't (the bar can't know its length), or a scene with no tour.
  useEffect(() => {
    if (!pos || !data) return;
    if (!tour) setTour(null);
    else if (pos.step >= tour.steps.length) setTour({ ...pos, step: tour.steps.length - 1 });
  }, [pos, data, tour, setTour]);
  const step = tour && pos ? tour.steps[pos.step] : undefined;
  const item = step && data ? data.items.find((it) => it.id === step.card) : undefined;
  const last = !!tour && !!pos && pos.step === tour.steps.length - 1;
  // A scene tour's last stop hands over to the next scene's tour (the tabs' order); the written tour and the last scene end.
  const handOff = last && tour && data && isSceneTourId(tour.id) ? nextSceneTour(tour.id, data) : null;
  // A step on a journey view (Powers of Ten) holds for the journey: its clock runs from the moment the journey is on, not from
  // the scene settling (a cold cache took 19 s from Next to `on`, and the step moved on 6 s into the dive; the lead, 10-03), and
  // holds 3 s past the journey's end. While the journey waits or starts, the clock holds; stopped to look around, it holds too.
  // Where the viewport can't play it (the 2D preview, an older build), the step's own hold runs from the scene settling.
  const onJourney = useAtlas((s) => !!journeyOf(s));
  const jStatus = useJourney((s) => s.status);
  const jDuration = useJourney((s) => s.reply?.durationS ?? null);
  const jPlays = onJourney && jStatus !== 'unavailable' && jStatus !== 'error';
  // How far the journey has played, in hundredths (the scene's clock is the journey's); −1 off a journey.
  const jProgress = useAtlas((s) => (jPlays && jDuration ? Math.min(100, Math.floor((s.time / jDuration) * 100)) : -1));
  const jEnded = jPlays && jStatus === 'on' && jProgress >= 99;
  const hold = jPlays ? JOURNEY_TAIL_S : step ? holdOf(step) : HOLD_S;
  // The clock runs once the scene has what it draws, not under a sheet or when paused; the last step stays. On a journey it
  // runs once the journey has played to its end (the streaming of a stage can stall the dive), for the tail alone.
  const running = !!pos && !pos.paused && !covered && settled && !last && (!jPlays || jEnded);
  const stepKey = pos ? `${pos.id}.${pos.step}` : '';
  useEffect(() => {
    if (!running) return;
    const t = window.setTimeout(() => tourGo(1), hold * 1000);
    return () => clearTimeout(t);
  }, [running, stepKey, hold]);

  if (!pos || !tour || !step) return null;
  const n = tour.steps.length;
  const sc = item?.scene ? SCENARIOS.find((x) => x.id === item.scene) : undefined;
  return (
    <aside className="tour" role="region" aria-label={`Tour: ${tour.title}`} data-label-exclude="">
      <div className="tour-progress" aria-hidden="true">
        {tour.steps.map((_, i) => (
          <span key={i} className={`tour-seg ${i < pos.step ? 'done' : i === pos.step ? 'now' : ''}`}>
            {i === pos.step ? (
              <span
                key={`${stepKey}:${running}`}
                className={`tour-fill ${last ? 'full' : ''} ${jPlays ? 'journey' : ''}`}
                style={jPlays ? { transform: `scaleX(${jEnded ? 1 : Math.max(0, jProgress) / 100})` } : { animationDuration: `${hold}s`, animationPlayState: running ? 'running' : 'paused' }}
              />
            ) : null}
          </span>
        ))}
      </div>
      <div className="tour-body">
        <div className="tour-copy">
          <p className="tour-eyebrow ba-type-caption">
            <span>{`${tour.title} · ${pos.step + 1} of ${n}`}</span>
            {sc ? (
              <span className="tour-scene" style={{ ['--sc' as string]: `var(--sc-${sc.id})` }}>
                <span className="dot" aria-hidden="true" />
                {sc.name}
              </span>
            ) : null}
            {item ? <span className={`gstate gstate-${item.state}`}>{STATE_LABEL[item.state]}</span> : null}
          </p>
          <h3 className="tour-title">{item?.title ?? (data ? step.card : 'Loading the gallery')}</h3>
          <p className="tour-caption ba-type-small">{step.caption}</p>
        </div>
        <div className="tour-actions">
          <IconButton size="sm" icon={ChevronLeft} label="Back a step" keys={['⇧', '↵']} disabled={pos.step === 0} onClick={() => tourGo(-1)} />
          <IconButton size="sm" icon={pos.paused ? Play : Pause} label={pos.paused ? 'Play: the tour moves on by itself' : 'Pause: stay on this step'} onClick={() => setTour({ ...pos, paused: !pos.paused })} />
          {item ? (
            <Button variant="quiet" onClick={() => useAtlas.getState().setGallery(true, item.id)}>
              Read more
            </Button>
          ) : null}
          <Button variant="primary" icon={last && !handOff ? undefined : ChevronRight} onClick={() => (handOff ? startTour(handOff.id) : tourGo(1))}>
            {handOff ? `Next: ${handOff.name}` : last ? 'Done' : 'Next'}
          </Button>
          <IconButton size="sm" icon={X} label="Close the tour" keys={['Esc']} onClick={() => setTour(null)} />
        </div>
      </div>
    </aside>
  );
}

/** The gallery, read a moment after mount (the scene's own files come first), so a panel can say what a scene's tour has. */
export function useGalleryLater(): GalleryData | null {
  const [data, setData] = useState<GalleryData | null>(galleryIfLoaded);
  useEffect(() => {
    if (data) return;
    let on = true;
    const t = window.setTimeout(() => void loadGallery().then((g) => on && setData(g)), 1500);
    return () => {
      on = false;
      clearTimeout(t);
    };
  }, [data]);
  return data;
}

/** The left panel's link at the end of the scene's name row: the scene's own tour and how many stops it has, once the
 *  gallery says it has one; pressed while it runs, when it ends the tour instead (⇧T does the same). It sits in the name
 *  row rather than under the tagline so the header keeps its height: Explore's panel must fit without scrolling. */
export function SceneTourLink() {
  const scenario = useAtlas((s) => s.scenario);
  const on = useAtlas((s) => s.tour?.id === s.scenario);
  const data = useGalleryLater();
  const tour = data ? sceneTour(scenario, data) : null;
  if (!tour) return null;
  return (
    <Tip label={on ? 'End the scene’s tour' : tour.blurb} keys={['⇧', 'T']} side="right">
      <button type="button" className={`scene-tour ba-type-caption ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => (on ? useAtlas.getState().setTour(null) : startTour(scenario))}>
        <Route size={12} strokeWidth={1.5} aria-hidden="true" />
        {on ? 'Touring' : `Tour · ${tour.steps.length} stops`}
      </button>
    </Tip>
  );
}
